import { useEffect, useState } from "react";
import {
  BarChart3, Download, FileText, TrendingUp, Building2, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import jsPDF from "jspdf";
import { FENDER_LOGO_DATA_URL } from "@/lib/logoData";

const EXPORTS = [
  { k: "garages",        label: "All garages",       hint: "Every registered tenant with contact + creation date" },
  { k: "users",          label: "Workshop users",    hint: "All non-admin users across garages (no passwords)" },
  { k: "enquiries",      label: "Parts enquiries",   hint: "Every spare-parts enquiry with counts" },
  { k: "support_cases",  label: "Support cases",     hint: "Support tickets with status, priority, assignee" },
  { k: "sync_events",    label: "Sync events",       hint: "Client-reported offline queue events" },
];

// Brand colors matching pdf.js
const DEEP_SPACE = [29, 53, 87];
const STRAWBERRY = [230, 57, 70];
const STEEL = [69, 123, 157];
const FROSTED = [168, 218, 220];
const MUTED = [113, 128, 145];

const applyBranding = (doc, subtitle) => {
  doc.setFillColor(...DEEP_SPACE);
  doc.rect(0, 0, 210, 26, "F");
  doc.setFillColor(...STRAWBERRY);
  doc.rect(0, 26, 210, 1.5, "F");
  try { doc.addImage(FENDER_LOGO_DATA_URL, "PNG", 8, 5, 15, 11); } catch (e) { /* ok */ }
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text("Fender", 26, 13);
  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...FROSTED);
  doc.text("by SparesHub", 26, 18);
  doc.setFontSize(6);
  doc.text("PLATFORM OPERATIONS REPORT", 26, 22);
  if (subtitle) {
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text(subtitle, 202, 13, { align: "right" });
  }
  doc.setTextColor(0, 0, 0);
};

const buildPdf = (snap) => {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  applyBranding(doc, "Weekly Snapshot");

  const period = snap.period || {};
  const t = snap.totals || {};
  const g = snap.growth || {};
  const a = snap.activity || {};

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("Weekly Platform Snapshot", 10, 40);

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...MUTED);
  const w1 = (period.week_start || "").slice(0, 10);
  const w2 = (period.week_end || "").slice(0, 10);
  doc.text(`Period: ${w1} → ${w2}`, 10, 46);
  doc.text(`Generated: ${fmtDateTime(period.generated_at)} by ${period.generated_by}`, 10, 50);

  // Totals cards
  let y = 60;
  doc.setDrawColor(...FROSTED);
  const card = (x, y, w, h, label, value) => {
    doc.setDrawColor(228, 231, 236);
    doc.setLineWidth(0.3);
    doc.roundedRect(x, y, w, h, 2, 2);
    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    doc.setFont("helvetica", "bold");
    doc.text(label.toUpperCase(), x + 3, y + 5);
    doc.setFontSize(16);
    doc.setTextColor(...DEEP_SPACE);
    doc.text(String(value ?? "—"), x + 3, y + 13);
  };
  card(10, y, 60, 18, "Garages registered", t.garages_registered);
  card(75, y, 60, 18, "Active this week",   t.active_garages_this_week);
  card(140, y, 60, 18, "Total users",       t.total_users);

  // Growth table
  y += 24;
  doc.setFontSize(8);
  doc.setTextColor(...STEEL);
  doc.setFont("helvetica", "bold");
  doc.text("GROWTH — WEEK OVER WEEK", 10, y);
  doc.setDrawColor(...STRAWBERRY);
  doc.setLineWidth(0.4);
  doc.line(10, y + 1, 200, y + 1);
  y += 6;
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text("Metric", 10, y);
  doc.text("This week", 100, y, { align: "right" });
  doc.text("Prev week", 140, y, { align: "right" });
  doc.text("Δ %", 180, y, { align: "right" });
  y += 4;
  doc.setDrawColor(...FROSTED);
  doc.setLineWidth(0.2);
  doc.line(10, y, 200, y);
  y += 3;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const rows = [
    ["New garages", g.new_garages],
    ["New users",   g.new_users],
    ["Job cards",   g.job_cards],
  ];
  rows.forEach(([label, m]) => {
    const cur = m?.cur ?? 0;
    const prev = m?.prev ?? 0;
    const pct = m?.pct;
    doc.text(label, 10, y);
    doc.text(String(cur), 100, y, { align: "right" });
    doc.text(String(prev), 140, y, { align: "right" });
    if (pct === null || pct === undefined) {
      doc.setTextColor(...MUTED);
      doc.text("—", 180, y, { align: "right" });
    } else {
      doc.setTextColor(...(pct >= 0 ? [18, 183, 106] : [230, 57, 70]));
      doc.text(`${pct >= 0 ? "+" : ""}${pct}%`, 180, y, { align: "right" });
    }
    doc.setTextColor(0, 0, 0);
    y += 5.5;
  });

  // Activity strip
  y += 6;
  doc.setFontSize(8);
  doc.setTextColor(...STEEL);
  doc.setFont("helvetica", "bold");
  doc.text("ACTIVITY THIS WEEK", 10, y);
  doc.setDrawColor(...STRAWBERRY);
  doc.setLineWidth(0.4);
  doc.line(10, y + 1, 200, y + 1);
  y += 6;
  card(10, y, 60, 16, "Enquiries",      a.enquiries_this_week);
  card(75, y, 60, 16, "Open cases",     a.open_support_cases);
  card(140, y, 60, 16, "Sync events",   a.sync_events_this_week);

  // Top garages
  y += 22;
  doc.setFontSize(8);
  doc.setTextColor(...STEEL);
  doc.setFont("helvetica", "bold");
  doc.text("TOP GARAGES BY JOB CARDS (THIS WEEK)", 10, y);
  doc.setDrawColor(...STRAWBERRY);
  doc.setLineWidth(0.4);
  doc.line(10, y + 1, 200, y + 1);
  y += 6;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  const top = snap.top_garages_by_jc || [];
  if (top.length === 0) {
    doc.setTextColor(...MUTED);
    doc.text("No job-card activity yet this week.", 10, y);
  } else {
    top.forEach((row, i) => {
      doc.setFont("helvetica", "bold");
      doc.text(`${i + 1}. ${row.garage_name}`, 10, y);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...MUTED);
      doc.text(row.city || "", 100, y);
      doc.setTextColor(...DEEP_SPACE);
      doc.text(`${row.jc} JC`, 200, y, { align: "right" });
      doc.setTextColor(0, 0, 0);
      y += 5.5;
    });
  }

  // Footer
  const h = doc.internal.pageSize.getHeight();
  doc.setDrawColor(...FROSTED);
  doc.line(10, h - 12, 200, h - 12);
  doc.setFontSize(7);
  doc.setTextColor(...MUTED);
  doc.text("Fender by SparesHub · fenderapp.com · © 2026 Iradium Automobiles Private Limited · Internal report, do not distribute", 10, h - 7);

  return doc;
};

const KpiTile = ({ label, value, sub, tone = "steel" }) => {
  const map = {
    steel: "bg-[#a8dadc]/30 text-[#457b9d]",
    red: "bg-[#e63946]/10 text-[#e63946]",
    orange: "bg-[#F79009]/10 text-[#F79009]",
    green: "bg-[#12B76A]/10 text-[#12B76A]",
  };
  return (
    <div className="bg-white rounded-xl border border-[#E4E7EC] p-4">
      <div className={`w-9 h-9 rounded-lg grid place-items-center mb-3 ${map[tone]}`}>
        <BarChart3 size={17} strokeWidth={2} />
      </div>
      <div className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">{label}</div>
      <div className="text-[26px] md:text-[30px] font-bold text-[#1d3557] leading-tight mt-1 font-mono">{value ?? "—"}</div>
      {sub && <div className="text-[11px] text-[#667085] mt-1">{sub}</div>}
    </div>
  );
};

const GrowthRow = ({ label, m }) => {
  const pct = m?.pct;
  const tone = pct === null || pct === undefined ? "text-[#667085]" : pct >= 0 ? "text-[#12B76A]" : "text-[#e63946]";
  return (
    <tr>
      <td className="px-4 py-3 text-[13px] text-[#1d3557] font-semibold">{label}</td>
      <td className="px-4 py-3 text-right font-mono text-[#1d3557]">{m?.cur ?? 0}</td>
      <td className="px-4 py-3 text-right font-mono text-[#667085]">{m?.prev ?? 0}</td>
      <td className={`px-4 py-3 text-right font-mono font-bold ${tone}`}>
        {pct === null || pct === undefined ? "—" : `${pct >= 0 ? "+" : ""}${pct}%`}
      </td>
    </tr>
  );
};

export default function AdminReports() {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState("");
  const [jobs, setJobs] = useState([]);

  const load = () => {
    setLoading(true);
    api.get("/admin/reports/weekly-snapshot")
      .then((r) => setSnap(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  const loadJobs = () => {
    api.get("/admin/reports/exports").then((r) => setJobs(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); loadJobs(); }, []);
  // Poll for pending jobs
  useEffect(() => {
    const anyPending = jobs.some((j) => j.status === "queued");
    if (!anyPending) return;
    const t = setInterval(loadJobs, 2500);
    return () => clearInterval(t);
  }, [jobs]);

  const downloadPdf = () => {
    if (!snap) return;
    const doc = buildPdf(snap);
    doc.save(`fender-weekly-snapshot-${(snap.period?.week_end || "").slice(0, 10)}.pdf`);
  };

  const queueExport = async (module) => {
    setExporting(module);
    try {
      const r = await api.post(`/admin/reports/exports?module=${module}`);
      toast.success(`Queued ${module} export…`);
      setJobs((prev) => [r.data, ...prev]);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExporting(""); }
  };

  const downloadJob = async (j) => {
    try {
      const r = await api.get(`/admin/reports/exports/${j.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${j.module}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-reports-root">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Governance</div>
          <h1 className="text-[26px] md:text-[30px] font-bold text-[#1d3557] flex items-center gap-2">
            <BarChart3 size={22} className="text-[#457b9d]" /> Reports & Exports
          </h1>
          <p className="text-[13px] text-[#667085] mt-0.5">
            One-click weekly PDF snapshots and CSV exports of every core module.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={load}
            data-testid="admin-reports-refresh"
            className="h-9 bg-white border border-[#E4E7EC] hover:border-[#457b9d] text-[#1d3557] rounded-md font-semibold text-[13px] px-3"
          >
            <RefreshCw size={13} className="mr-1.5" /> Refresh
          </Button>
          <Button
            onClick={downloadPdf}
            disabled={!snap}
            data-testid="admin-reports-download-pdf"
            className="h-9 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold text-[13px] px-3"
          >
            <FileText size={13} className="mr-1.5" /> Download weekly PDF
          </Button>
        </div>
      </div>

      {loading && <div className="text-[13px] text-[#667085]">Loading snapshot…</div>}
      {snap && (
        <>
          {/* Period banner */}
          <div className="bg-gradient-to-br from-[#1d3557] to-[#0f2140] rounded-xl p-5 text-white" data-testid="admin-reports-period">
            <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#a8dadc] mb-1">Weekly Snapshot</div>
            <div className="text-[22px] font-bold">{(snap.period?.week_start || "").slice(0, 10)} → {(snap.period?.week_end || "").slice(0, 10)}</div>
            <div className="text-[11px] text-[#a8dadc] mt-1 font-mono">
              Generated {fmtDateTime(snap.period?.generated_at)} · {snap.period?.generated_by}
            </div>
          </div>

          {/* Totals */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <KpiTile label="Garages registered"     value={snap.totals?.garages_registered} />
            <KpiTile label="Active this week"       value={snap.totals?.active_garages_this_week} tone="green" />
            <KpiTile label="Total workshop users"   value={snap.totals?.total_users} tone="orange" />
          </div>

          {/* Growth table */}
          <div className="bg-white rounded-xl border border-[#E4E7EC] overflow-hidden" data-testid="admin-reports-growth">
            <div className="px-5 py-3.5 border-b border-[#F1FAEE]">
              <h3 className="text-[15px] font-semibold text-[#1d3557] flex items-center gap-2">
                <TrendingUp size={15} className="text-[#457b9d]" /> Growth — week over week
              </h3>
            </div>
            <table className="w-full text-[13px]">
              <thead className="bg-[#F7F9FC] text-[11px] uppercase tracking-wider text-[#667085]">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Metric</th>
                  <th className="text-right px-4 py-3 font-semibold">This week</th>
                  <th className="text-right px-4 py-3 font-semibold">Prev week</th>
                  <th className="text-right px-4 py-3 font-semibold">Δ %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1FAEE]">
                <GrowthRow label="New garages" m={snap.growth?.new_garages} />
                <GrowthRow label="New users"   m={snap.growth?.new_users} />
                <GrowthRow label="Job cards"   m={snap.growth?.job_cards} />
              </tbody>
            </table>
          </div>

          {/* Activity + Top garages */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-[#E4E7EC] p-5" data-testid="admin-reports-activity">
              <h3 className="text-[15px] font-semibold text-[#1d3557] mb-3">Activity this week</h3>
              <div className="grid grid-cols-3 gap-3">
                <MiniStat label="Enquiries"     value={snap.activity?.enquiries_this_week} />
                <MiniStat label="Open cases"    value={snap.activity?.open_support_cases} />
                <MiniStat label="Sync events"   value={snap.activity?.sync_events_this_week} />
              </div>
            </div>
            <div className="bg-white rounded-xl border border-[#E4E7EC] p-5" data-testid="admin-reports-top">
              <h3 className="text-[15px] font-semibold text-[#1d3557] mb-3 flex items-center gap-2">
                <Building2 size={15} className="text-[#457b9d]" /> Top garages by JC
              </h3>
              {(snap.top_garages_by_jc || []).length === 0 ? (
                <div className="text-[13px] text-[#667085]">No garage activity yet this week.</div>
              ) : (
                <ol className="space-y-2">
                  {(snap.top_garages_by_jc || []).map((g, i) => (
                    <li key={g.garage_id} className="flex items-center justify-between text-[13px]">
                      <div>
                        <span className="text-[#e63946] font-bold mr-2">{i + 1}.</span>
                        <span className="text-[#1d3557] font-semibold">{g.garage_name}</span>
                        <span className="text-[#667085] ml-2 text-[12px]">{g.city}</span>
                      </div>
                      <span className="text-[#e63946] font-mono font-bold">{g.jc} JC</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </>
      )}

      {/* CSV Exports */}
      <div className="bg-white rounded-xl border border-[#E4E7EC] overflow-hidden" data-testid="admin-reports-exports">
        <div className="px-5 py-3.5 border-b border-[#F1FAEE]">
          <h3 className="text-[15px] font-semibold text-[#1d3557] flex items-center gap-2">
            <Download size={15} className="text-[#457b9d]" /> Async CSV exports (role-masked)
          </h3>
          <div className="text-[11px] text-[#667085] mt-0.5">
            Columns are automatically masked based on your admin role. Downloads sit in the queue below for 24 hours.
          </div>
        </div>
        <div className="divide-y divide-[#F1FAEE]">
          {EXPORTS.map((ex) => (
            <div key={ex.k} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`admin-export-row-${ex.k}`}>
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-[#1d3557]">{ex.label}</div>
                <div className="text-[11px] text-[#667085]">{ex.hint}</div>
              </div>
              <Button
                onClick={() => queueExport(ex.k)}
                disabled={exporting === ex.k}
                data-testid={`admin-export-queue-${ex.k}`}
                className="h-9 bg-white border border-[#E4E7EC] hover:border-[#e63946] text-[#1d3557] rounded-md font-semibold text-[13px] px-3"
              >
                <Download size={13} className="mr-1.5" /> {exporting === ex.k ? "Queuing…" : "Queue export"}
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Export queue */}
      <div className="bg-white rounded-xl border border-[#E4E7EC] overflow-hidden" data-testid="admin-export-queue">
        <div className="px-5 py-3.5 border-b border-[#F1FAEE] flex items-center justify-between">
          <div>
            <h3 className="text-[15px] font-semibold text-[#1d3557]">Recent exports</h3>
            <div className="text-[11px] text-[#667085] mt-0.5">{jobs.length} recent job(s)</div>
          </div>
          <button
            onClick={loadJobs}
            data-testid="admin-export-queue-refresh"
            className="text-[12px] text-[#457b9d] hover:text-[#e63946] font-semibold inline-flex items-center gap-1"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
        <table className="w-full text-[13px]">
          <thead className="bg-[#F7F9FC] text-[11px] uppercase tracking-wider text-[#667085]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Module</th>
              <th className="text-left px-4 py-3 font-semibold">Requested by</th>
              <th className="text-right px-4 py-3 font-semibold">Rows</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-left px-4 py-3 font-semibold">Ready</th>
              <th className="text-right px-4 py-3 font-semibold">Download</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F1FAEE]">
            {jobs.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[13px] text-[#667085]">
                No export jobs yet. Queue one from the list above.
              </td></tr>
            )}
            {jobs.map((j) => (
              <tr key={j.id} className="hover:bg-[#F7F9FC]" data-testid={`admin-export-job-${j.id}`}>
                <td className="px-4 py-3 font-semibold text-[#1d3557] capitalize">{j.module.replace(/_/g, " ")}</td>
                <td className="px-4 py-3 text-[12px] text-[#667085] font-mono">{j.requested_by}</td>
                <td className="px-4 py-3 text-right font-mono text-[#1d3557]">{j.rows ?? "—"}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                    j.status === "ready" ? "bg-[#12B76A]/10 text-[#12B76A]" :
                    j.status === "queued" ? "bg-[#F79009]/10 text-[#F79009]" :
                    j.status === "failed" ? "bg-[#e63946]/10 text-[#e63946]" :
                    "bg-[#66708515] text-[#667085]"
                  }`}>{j.status}</span>
                </td>
                <td className="px-4 py-3 text-[11px] font-mono text-[#667085]">{j.ready_at ? fmtDateTime(j.ready_at) : "—"}</td>
                <td className="px-4 py-3 text-right">
                  {j.status === "ready" ? (
                    <button
                      onClick={() => downloadJob(j)}
                      data-testid={`admin-export-download-${j.id}`}
                      className="text-[12px] text-[#e63946] hover:underline font-semibold inline-flex items-center gap-1"
                    >
                      <Download size={11} /> CSV
                    </button>
                  ) : (
                    <span className="text-[12px] text-[#a8dadc]">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const MiniStat = ({ label, value }) => (
  <div className="text-center">
    <div className="text-[24px] font-bold text-[#1d3557] font-mono">{value ?? 0}</div>
    <div className="text-[11px] text-[#667085] uppercase tracking-wider font-semibold mt-1">{label}</div>
  </div>
);
